import type { ReactNode } from "react";
import type { QueryState } from "../api";
import { errorMessage, isProblemError, isRetryable } from "../api";
import { Button, Skeleton } from "../components";
import styles from "./AsyncBoundary.module.css";

export type AsyncBoundaryProps<T> = {
  /** Ce que rend `useApiQuery`. */
  state: QueryState<T>;
  /**
   * Silhouette affichée pendant le premier chargement. Défaut : trois lignes.
   * **Toujours en fournir une qui ressemble au contenu attendu** — c'est ce qui
   * évite le saut de mise en page à l'arrivée des données.
   */
  skeleton?: ReactNode;
  /** Vrai quand la réponse est valide mais ne contient rien à montrer. */
  isEmpty?: (data: T) => boolean;
  /** Rendu quand `isEmpty` est vrai. Un `EmptyState`, en général. */
  empty?: ReactNode;
  /** Titre du bloc d'erreur. Défaut : « Chargement impossible ». */
  errorTitle?: string;
  /** Rendu avec les données, une fois qu'il y en a. */
  children: (data: T) => ReactNode;
};

/**
 * Les trois états d'une lecture, traités une fois pour toutes : chargement,
 * erreur, vide.
 *
 * ```tsx
 * const etat = useApiQuery((o) => api.listPacks(o), []);
 * <AsyncBoundary
 *   state={etat}
 *   skeleton={<Skeleton variant="block" height="12rem" />}
 *   isEmpty={(d) => d.packs.length === 0}
 *   empty={<EmptyState title="Aucun pack en vente." />}
 * >
 *   {(d) => <ListePacks packs={d.packs} />}
 * </AsyncBoundary>
 * ```
 *
 * Les données déjà affichées survivent à un rechargement raté : on préfère un
 * solde d'il y a dix secondes à un écran d'erreur qui efface tout.
 */
export function AsyncBoundary<T>({
  state,
  skeleton,
  isEmpty,
  empty,
  errorTitle = "Chargement impossible",
  children,
}: AsyncBoundaryProps<T>) {
  if (state.data === null && state.loading) {
    return <>{skeleton ?? <Skeleton lines={3} />}</>;
  }

  if (state.data === null && state.error !== null) {
    const erreur = state.error;
    return (
      <div className={styles.erreur} role="alert">
        <p className={styles.titre}>{errorTitle}</p>
        <p className={styles.detail}>{errorMessage(erreur)}</p>
        {isProblemError(erreur) && erreur.requestId !== undefined && (
          <p className={styles.reference}>
            Référence à communiquer au support : {erreur.requestId}
          </p>
        )}
        {isRetryable(erreur) && (
          <Button variant="secondary" onClick={state.reload} loading={state.loading}>
            Réessayer
          </Button>
        )}
      </div>
    );
  }

  if (state.data === null) {
    return null;
  }

  if (isEmpty?.(state.data) === true) {
    return <>{empty ?? null}</>;
  }

  return <>{children(state.data)}</>;
}
